/**
 * On-screen console log viewer for phones without USB debugging access.
 * Gated by the backend's SIGNIT_DEBUG_PANEL env var (see config.py /
 * GET /api/v1/config), checked once on load -- flip it off in the
 * Deployment's ConfigMap and restart the pods to turn this off again,
 * no image rebuild needed.
 *
 * Captures console.log/debug/warn/error plus uncaught errors and unhandled
 * promise rejections into an in-memory ring buffer, rendered in a toggle-able
 * overlay panel with a copy-to-clipboard button (so a captured log can be
 * pasted elsewhere without needing to select text on a phone screen).
 */

const MAX_ENTRIES = 300;

interface LogEntry {
  level: "log" | "debug" | "warn" | "error";
  time: string;
  text: string;
}

/** JSON.stringify renders ArrayBuffers as `{}`, which is indistinguishable
 * from an absent value -- exactly the ambiguity that matters when inspecting
 * WebAuthn extension results. Hex-dump buffers instead. */
function replaceBuffers(value: unknown): unknown {
  if (value instanceof ArrayBuffer || ArrayBuffer.isView(value)) {
    const bytes = new Uint8Array(
      value instanceof ArrayBuffer ? value : (value as ArrayBufferView).buffer,
    );
    return `<${bytes.length} bytes: ${[...bytes]
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")}>`;
  }
  if (Array.isArray(value)) return value.map(replaceBuffers);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, replaceBuffers(v)]),
    );
  }
  return value;
}

function formatArg(arg: unknown): string {
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
  if (typeof arg === "string") return arg;
  try {
    return JSON.stringify(replaceBuffers(arg));
  } catch {
    return String(arg);
  }
}

function timestamp(): string {
  const d = new Date();
  return d.toTimeString().slice(0, 8) + "." + String(d.getMilliseconds()).padStart(3, "0");
}

export async function initDebugPanel(): Promise<void> {
  let enabled = false;
  try {
    const resp = await fetch("/api/v1/config");
    enabled = (await resp.json()).debugPanel === true;
  } catch {
    return; // config endpoint unreachable -- fail closed, no panel
  }
  if (!enabled) return;

  const entries: LogEntry[] = [];

  const toggleBtn = document.createElement("button");
  toggleBtn.id = "debug-panel-toggle";
  toggleBtn.type = "button";
  toggleBtn.textContent = "🐞";

  const panel = document.createElement("div");
  panel.id = "debug-panel";
  panel.hidden = true;

  const header = document.createElement("div");
  header.id = "debug-panel-header";
  const clearBtn = document.createElement("button");
  clearBtn.type = "button";
  clearBtn.textContent = "Leeren";
  const copyBtn = document.createElement("button");
  copyBtn.type = "button";
  copyBtn.textContent = "Kopieren";
  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.textContent = "✕";
  header.append(document.createTextNode("Debug-Log "), clearBtn, copyBtn, closeBtn);

  const logEl = document.createElement("pre");
  logEl.id = "debug-panel-log";

  panel.append(header, logEl);
  document.body.append(toggleBtn, panel);

  function render(): void {
    logEl.textContent = entries
      .map((e) => `[${e.time}] ${e.level.toUpperCase()}: ${e.text}`)
      .join("\n");
    logEl.scrollTop = logEl.scrollHeight;
  }

  function push(level: LogEntry["level"], args: unknown[]): void {
    entries.push({ level, time: timestamp(), text: args.map(formatArg).join(" ") });
    if (entries.length > MAX_ENTRIES) entries.shift();
    if (!panel.hidden) render();
  }

  toggleBtn.addEventListener("click", () => {
    panel.hidden = !panel.hidden;
    if (!panel.hidden) render();
  });
  closeBtn.addEventListener("click", () => {
    panel.hidden = true;
  });
  clearBtn.addEventListener("click", () => {
    entries.length = 0;
    render();
  });
  copyBtn.addEventListener("click", () => {
    void navigator.clipboard.writeText(entries.map((e) => `[${e.time}] ${e.level}: ${e.text}`).join("\n"));
  });

  (["log", "debug", "warn", "error"] as const).forEach((level) => {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      original(...args);
      push(level, args);
    };
  });

  window.addEventListener("error", (event) => {
    push("error", [`Uncaught: ${event.message}`]);
  });
  window.addEventListener("unhandledrejection", (event) => {
    push("error", ["Unhandled promise rejection:", event.reason]);
  });

  push("log", ["Debug panel aktiv (SIGNIT_DEBUG_PANEL)."]);
}
